import view from '../../view';
import blocks from '../../blocks';
import hooks from '../../hooks';
import hdrm from '../../hdrm';
import copyCssTextForBlock from '../helpers/custom-colors/copy-css-text-for-block';
import getCustomTextColorsStylesheet from '../helpers/custom-colors/get-custom-text-color-stylesheet';
import updateCustomColorStylesheetInGlobal from '../helpers/custom-colors/update-custom-color-stylesheet-in-global';
import getElementCustomColorVariables from '../helpers/custom-colors/get-element-custom-color-variables';
import createCustomTextColorStylesheet from '../helpers/custom-colors/create-custom-text-color-stylesheet';
import setRuleForSelector from '../helpers/custom-colors/set-rule-for-selector';
import clipboard from '../../clipboard';
import customization from '../../customization';
import { UPDATE_STATIC_ONLY } from '../../hdrm/constants/update-strategies';

const getTargetAndCopiedBlocks = (state, action) => {
  switch (action.type) {
    case view.actionTypes.DUPLICATE_ELEMENT: {
      const { elementId } = action.payload;
      const staticTargetElement = view.accessors.getStaticElement(elementId);
      return {
        targetBlock: staticTargetElement,
        copiedBlock: staticTargetElement.nextElementSibling,
      };
    }

    case view.actionTypes.PASTE_ELEMENT: {
      const {
        refElementId,
        position,
        options: { isBlockReplaced },
      } = action.payload;
      const staticTargetElement = view.accessors.getStaticElement(refElementId);
      const { element: copiedElement } = clipboard.selectors.getClipboardData(state);
      let staticPastedElement;

      if (isBlockReplaced) {
        const staticDocument = hdrm.accessors.getStaticDocument();
        const blockId = blocks.accessors.getBlockIdByElement(copiedElement);
        const targetElementConstraint = blocks.selectors.getBlockConstraint(state, blockId);
        staticPastedElement = staticDocument.querySelector(targetElementConstraint.element);
      } else {
        staticPastedElement =
          position === 'afterend'
            ? staticTargetElement.nextElementSibling
            : staticTargetElement.previousSibling;
      }

      return {
        targetBlock: copiedElement,
        copiedBlock: staticPastedElement,
      };
    }
    default: {
      return {
        targetBlock: null,
        copiedBlock: null,
      };
    }
  }
};

const pasteCustomColors = (customColorsData, targetBlockId, copiedBlockId) => {
  const { cssRuleObj, cssVariables } = customColorsData;
  let stylesheet = getCustomTextColorsStylesheet();

  if (!stylesheet) {
    stylesheet = createCustomTextColorStylesheet();
  }

  const { cssRules } = stylesheet;
  let rootRule = Array.from(cssRules).find((rule) => rule.selectorText === ':root');

  if (!rootRule) {
    stylesheet.insertRule(`:root {}`, 0);
    [rootRule] = stylesheet.cssRules;
  }

  const selector = cssRuleObj.selector.replace(targetBlockId, copiedBlockId);

  setRuleForSelector(selector, stylesheet, cssRuleObj.property);

  cssVariables.forEach(([name, value]) => {
    rootRule.style.cssText += `${name}: ${value}`;
    customization.operations.addCustomTextColor({
      value,
      name,
    });
  });
};

const handlePasteElement = (action, state) => {
  const { options } = action.payload;
  const { strategy, customColorsData } = options;
  const { targetBlock, copiedBlock } = getTargetAndCopiedBlocks(state, action);
  const hasElementCustomColor = getElementCustomColorVariables(copiedBlock).length > 0;

  const duplicateCustomColors = () => {
    if (!hasElementCustomColor || !blocks.verifiers.isElementBlock(copiedBlock)) return;

    if (customColorsData) {
      pasteCustomColors(customColorsData, targetBlock.id, copiedBlock.id);
    }

    const customStylesheet = getCustomTextColorsStylesheet();

    copyCssTextForBlock(targetBlock.id, copiedBlock.id, customStylesheet);
    updateCustomColorStylesheetInGlobal();
  };

  if (strategy === UPDATE_STATIC_ONLY) {
    hooks.operations.subscribe(
      hooks.availableHooks.ON_LIVE_DOCUMENT_CONNECTED,
      duplicateCustomColors,
      { once: true }
    );
  } else {
    duplicateCustomColors();
  }
};

const customColors = (store) => (next) => (action) => {
  const state = store.getState();

  switch (action.type) {
    case view.actionTypes.PASTE_ELEMENT:
    case view.actionTypes.DUPLICATE_ELEMENT: {
      handlePasteElement(action, state);
      next(action);
      break;
    }

    case view.actionTypes.BULK_VIEW_ACTIONS: {
      const { actions } = action.payload;

      actions.forEach((receivedAction) => {
        if (receivedAction.type === view.actionTypes.PASTE_ELEMENT) {
          handlePasteElement(receivedAction, state);
        }
      });

      next(action);
      break;
    }

    default:
      next(action);
  }
};

export default customColors;
